<template>
  <div id="story-pieces-list">
    <div class="title">
      <font-awesome-icon :icon="faTypewriter" class="icon" />
      <h1>{{ t('STORY_PIECES') }}</h1>

      <router-link :to="applyParams(PATHS.STORY_PIECE_EDITOR, '')">
        <div class="add-btn">
          <cross white />
        </div>
      </router-link>
    </div>

    <div class="list" ref="listRef">
      <div class="row">
        <div
          class="col col-xs-12 col-md-6 col-lg-4"
          v-for="(col, i) in itemsGrid"
          :key="i"
        >
          <div
            v-for="item in itemsGrid[i]"
            :key="item.id"
            class="item"
          >
            <div class="box">
              <div class="controls">
                <toggle-input
                  v-if="canPublish"
                  :value="item.published"
                  @click="toggleStoryPiece(item)"
                />
                <router-link :to="applyParams(PATHS.STORY_PIECE_EDITOR, item.id)">
                  <font-awesome-icon :icon="faPenAlt" class="icon" />
                </router-link>
                <font-awesome-icon :icon="faTrashAlt" class="icon" @click="removeStoryPiece(item.id)" v-if="canRemove" />
              </div>

              <router-link :to="applyParams(PATHS.STORY_PIECE_EDITOR, item.id)">
                <h3>{{ item.title }}</h3>
                <div class="img-holder" v-if="!!item.image">
                  <img :src="item.image.url" :alt="item.title" />
                </div>
              </router-link>
              <p>{{ item.content }}</p>
              <div class="date">{{ moment(item.updatedAt).format(`${locale === 'en' ? 'DD MMM, YYYY' : 'DD [tháng] MM, YYYY'} | HH:ss`) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, computed, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import moment from 'moment';
import $ from 'jquery';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTypewriter, faTrashAlt, faPenAlt } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import Cross from '@/components/Cross';
import ToggleInput from '@/components/ToggleInput';

import PATHS from '@/consts/paths';
import PERMISSIONS from '@/consts/permissionCodes';
import { applyParams } from '@/helpers';
import { hasPermissions } from '@/helpers/permission';

import StoryPiecesService from './StoryPiecesService';

export default {
  name: 'StoryPiecesList',
  components: {
    FontAwesomeIcon,
    Cross,
    ToggleInput
  },
  setup() {
    const { t, locale } = useI18n();
    const store = useStore();

    const listRef = ref(null);
    const filters = computed(() => store.state.storyPieces.filters);

    const canPublish = computed(() => hasPermissions([PERMISSIONS.ADMIN_MANAGE_STORY]));
    const canRemove = computed(() => hasPermissions([PERMISSIONS.ADMIN_MANAGE_STORY]));

    const calculateItemHeightScore = (item) => {
      let h = 0;
      if (item?.image) h += 300;
      if (item?.description) h += item.description.length;

      return h;
    };

    const items = computed(() => store.state.storyPieces.items);
    const itemsGrid = computed(() => {
      let n = 3;
      if (store.state.app.isTablet) {
        n = 2;
      } else if (store.state.app.isMobile) {
        n = 1;
      }

      const stacks = Array(n).fill().map(() => []);
      const stackHeights = Array(n).fill(0);

      for (let i = 0; i < items.value.length; i += 1) {
        const minStack = stackHeights.indexOf(Math.min(...stackHeights));
        stacks[minStack].push({ ...items.value[i] });
        stackHeights[minStack] += calculateItemHeightScore(items.value[i]);
      }

      return stacks;
    });

    const removeStoryPiece = async (id) => {
      try {
        await StoryPiecesService.removeStoryPiece(id);
        store.dispatch('storyPieces/getStoryPieces', { append: false });
        store.dispatch('flash/info', 'STORY_PIECE_SUCCESSFULLY_REMOVED');
      } catch(e) {
        store.dispatch('flash/error', e.message);
      }
    };

    const toggleStoryPiece = async (dish) => {
      const { id } = dish;

      try {
        if (dish.published) {
          await StoryPiecesService.toggleStoryPiece(id, false);
          store.dispatch('flash/info', 'STORY_PIECE_SUCCESSFULLY_UNPUBLISHED');
        } else {
          await StoryPiecesService.toggleStoryPiece(id, true);
          store.dispatch('flash/info', 'STORY_PIECE_SUCCESSFULLY_PUBLISHED');
        }
        store.dispatch('storyPieces/getStoryPieces', { append: false });
      } catch(e) {
        store.dispatch('flash/error', e.message);
      }
    };

    const watchLoadMore = () => {
      if (!listRef.value) return;

      const y = window.scrollY;
      const h = $(listRef.value).height();

      if (y + 200 > h) {
        store.dispatch('storyPieces/applyFilters', {
          page: filters.value.page + 1
        });
      }
    };

    watch(filters, () => {
      store.dispatch('storyPieces/getStoryPieces', { append: false });
    });

    onMounted(() => {
      store.dispatch('storyPieces/getStoryPieces', { append: false });

      $(window).on('scroll', watchLoadMore);
    });

    onUnmounted(() => {
      $(window).off('scroll', watchLoadMore);
    });

    return {
      t,
      faTypewriter,
      faPlus,
      itemsGrid,
      PATHS,
      faTrashAlt,
      faPenAlt,
      removeStoryPiece,
      applyParams,
      toggleStoryPiece,
      canPublish,
      canRemove,
      moment,
      locale,
      listRef
    };
  }
};
</script>

<style lang="scss">
@use "~@/assets/scss/colors" as *;

#story-pieces-list {
  .list {
    .item {
      .box {
        .controls {
          .toggle-input {
            .toggle-wrapper {
              &.active {
                background-color: $blue;
              }
            }
          }
        }

        .date {
          color: $grey;
          margin-top: 10px;
          font-size: 12px;
        }
      }
    }
  }
}
</style>
